import { useState, type ImgHTMLAttributes } from "react";
import { transformSupabaseImage } from "@/lib/image-transform";
import { cn } from "@/lib/utils";

/**
 * Responsive, format-negotiated image for public pages.
 *
 * - Emits AVIF + WebP `<source>` sets built from Supabase render transforms,
 *   with the original URL as the final fallback (non-Supabase URLs are
 *   passed through untouched).
 * - Always renders explicit width/height so the browser reserves layout
 *   space (zero CLS).
 * - Lazy + async-decoded by default; pass `priority` for LCP candidates.
 * - Fades in on load to avoid a hard pop-in.
 */
export type SmartImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet" | "loading" | "width" | "height"
> & {
  src: string | null | undefined;
  alt: string;
  /** Rendered box in CSS px at 1x — drives the generated width ladder. */
  width: number;
  height: number;
  /** `sizes` attribute; defaults to the intrinsic width. */
  sizes?: string;
  /** Candidate widths. Defaults to 1x/1.5x/2x of `width`. */
  widths?: number[];
  quality?: number;
  resize?: "cover" | "contain" | "fill";
  /** LCP candidate: eager, high fetch priority, no fade. */
  priority?: boolean;
  className?: string;
};

export function SmartImage({
  src,
  alt,
  width,
  height,
  sizes,
  widths,
  quality = 74,
  resize = "cover",
  priority = false,
  className,
  ...rest
}: SmartImageProps) {
  const [loaded, setLoaded] = useState(false);
  if (!src) return null;

  const ladder = (widths ?? [width, Math.round(width * 1.5), width * 2])
    .filter((w, i, a) => w > 0 && a.indexOf(w) === i)
    .sort((a, b) => a - b);

  const setFor = (format?: "avif" | "webp") =>
    ladder
      .map((w) => `${transformSupabaseImage(src, { width: w, quality, resize, format })} ${w}w`)
      .join(", ");

  const img = (
    <img
      {...rest}
      src={transformSupabaseImage(src, { width: ladder[ladder.length - 1], quality, resize })}
      srcSet={setFor()}
      sizes={sizes ?? `${width}px`}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      fetchPriority={priority ? "high" : "auto"}
      onLoad={() => setLoaded(true)}
      className={cn(
        className,
        !priority && "transition-opacity duration-300",
        !priority && !loaded && "opacity-0",
        !priority && loaded && "opacity-100",
      )}
    />
  );

  return (
    <picture>
      <source type="image/avif" srcSet={setFor("avif")} sizes={sizes ?? `${width}px`} />
      <source type="image/webp" srcSet={setFor("webp")} sizes={sizes ?? `${width}px`} />
      {img}
    </picture>
  );
}
